import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="h-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--cream)', padding: '24px' }}
        >
          <div className="text-center" style={{ maxWidth: '320px' }}>
            <div
              className="mx-auto flex items-center justify-center rounded-full"
              style={{
                width: '56px',
                height: '56px',
                backgroundColor: 'rgba(224, 123, 84, 0.1)',
                marginBottom: '16px',
              }}
            >
              <AlertTriangle size={24} style={{ color: 'var(--terracotta)' }} />
            </div>
            <h2
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '18px',
                color: 'var(--dark-teal)',
                marginBottom: '8px',
              }}
            >
              Algo salió mal
            </h2>
            <p
              style={{
                fontSize: '14px',
                color: 'var(--muted-fg)',
                lineHeight: 1.5,
                marginBottom: '20px',
              }}
            >
              Ocurrió un error inesperado. Intenta recargar la página.
            </p>
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 rounded-xl transition-colors"
              style={{
                backgroundColor: 'var(--terracotta)',
                color: 'white',
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                border: 'none',
              }}
            >
              <RefreshCw size={16} />
              Reintentar
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
